import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AgentRequest, AgentRequestFilters } from '../../types';
import apiService from '../../services/api';

interface ApproveRequestParams {
  requestId: string;
  adminNote?: string;
}

interface RejectRequestParams {
  requestId: string;
  adminNote: string;
}

interface AgentRequestsState {
  requests: AgentRequest[];
  selectedRequest: AgentRequest | null;
  filters: AgentRequestFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  counts: {
    pending: number;
    approved: number;
    rejected: number;
  };
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

const initialState: AgentRequestsState = {
  requests: [],
  selectedRequest: null,
  filters: {
    status: undefined,
    city: undefined,
    search: undefined,
    page: 1,
    limit: 20,
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  counts: {
    pending: 0,
    approved: 0,
    rejected: 0,
  },
  loading: false,
  actionLoading: false,
  error: null,
};

// Fetch agent requests
export const fetchAgentRequests = createAsyncThunk(
  'agentRequests/fetchAgentRequests',
  async (filters?: AgentRequestFilters) => {
    const response = await apiService.getAgentRequests(filters);
    return response;
  }
);

// Approve agent request
export const approveAgentRequest = createAsyncThunk(
  'agentRequests/approveAgentRequest',
  async (params: ApproveRequestParams) => {
    const response = await apiService.approveAgentRequest(params.requestId, params.adminNote);
    return response;
  }
);

// Reject agent request
export const rejectAgentRequest = createAsyncThunk(
  'agentRequests/rejectAgentRequest',
  async (params: RejectRequestParams) => {
    const response = await apiService.rejectAgentRequest(params.requestId, params.adminNote);
    return response;
  }
);

const agentRequestsSlice = createSlice({
  name: 'agentRequests',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<AgentRequestFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: undefined,
        city: undefined,
        search: undefined,
        page: 1,
        limit: 20,
      };
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    setSelectedRequest: (state, action: PayloadAction<AgentRequest | null>) => {
      state.selectedRequest = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch agent requests
    builder
      .addCase(fetchAgentRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentRequests.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success && action.payload.data) {
          state.requests = action.payload.data.requests;
          state.pagination = action.payload.data.pagination;
          state.counts = action.payload.data.counts;
        }
      })
      .addCase(fetchAgentRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch agent requests';
      });

    // Approve agent request
    builder
      .addCase(approveAgentRequest.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(approveAgentRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.success && action.payload.data) {
          // Update the request status in the list
          const index = state.requests.findIndex(r => r.id === action.payload.data?.id);
          if (index !== -1) {
            state.requests[index] = action.payload.data;
          }
          // Update counts
          if (state.counts.pending > 0) {
            state.counts.pending -= 1;
          }
          state.counts.approved += 1;
        }
      })
      .addCase(approveAgentRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || 'Failed to approve agent request';
      });

    // Reject agent request
    builder
      .addCase(rejectAgentRequest.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(rejectAgentRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.success && action.payload.data) {
          // Update the request status in the list
          const index = state.requests.findIndex(r => r.id === action.payload.data?.id);
          if (index !== -1) {
            state.requests[index] = action.payload.data;
          }
          // Update counts
          if (state.counts.pending > 0) {
            state.counts.pending -= 1;
          }
          state.counts.rejected += 1;
        }
      })
      .addCase(rejectAgentRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || 'Failed to reject agent request';
      });
  },
});

export const { setFilters, clearFilters, setPage, setSelectedRequest } = agentRequestsSlice.actions;
export default agentRequestsSlice.reducer;

